import './input.scss';
import './dropdownInput-v1-1.scss';
import '../menu/menu.scss';

import React, { useCallback, useState, useEffect, ReactNode } from 'react';
import { observer } from 'mobx-react-lite';
import find from 'lodash/find';
import classNames from 'classnames';

import { InputState } from './inputState';
import { ChevronDownSVGIcon, ChevronUpSVGIcon, InfoSVGIcon } from '../svg';
import { assert, Maybe, ObjectLiteral, VerticalPosition } from '../../core';
import { kebabCase } from 'change-case';
import { MenuItems } from './menuItems';
import { Button } from '../input';
import { Tooltip } from '../overlays';
import { useDelayedUnmount } from '../hooks';

export type DropdownItemObject = {
  value: string;
  label: string;
  description?: string;
  tooltip?: ReactNode;
  onDelete?: () => void;
};

type DropdownLayout = 'default' | 'plain' | 'filter';

export type DropdownItem = DropdownItemObject | string;

type Props = {
  className?: string | ObjectLiteral;
  dropdownClassName?: string | ObjectLiteral;
  value?: DropdownItem | null;
  label?: Maybe<string>;
  disabled?: boolean;
  isFocused?: boolean;
  isHovered?: boolean;
  checked?: boolean;
  readOnly?: boolean;
  message?: string;
  items: DropdownItem[];
  status?: Maybe<string>;
  statusMessage?: Maybe<string>;
  placeholder?: Maybe<string>;
  onChange?: (evt: React.SyntheticEvent, val: string) => void;
  onFocus?: React.FocusEventHandler;
  onBlur?: React.FocusEventHandler;
  onPointerEnter?: React.PointerEventHandler;
  onPointerLeave?: React.PointerEventHandler;
  showStatus?: boolean;
  labelPosition?: string;
  showFeedback?: Maybe<boolean>;
  feedbackPosition?: VerticalPosition;
  persistentFeedback?: boolean;
  layout?: DropdownLayout;
  notDeselectable?: boolean;
  withSearch?: boolean;
  tooltipContent?: string | ReactNode;
  virtualized?: boolean;
  'data-guided-training-id'?: string;
};

export const DropdownInput = observer(
  ({
    readOnly: _readOnly,
    value,
    className,
    dropdownClassName: _dropdownClassName,
    label,
    disabled,
    isFocused,
    isHovered,
    checked: _checked,
    message: _message,
    items,
    status,
    statusMessage: feedback,
    showStatus: _showStatus,
    labelPosition = 'topStart',
    showFeedback = true,
    feedbackPosition = 'bottom',
    persistentFeedback = true,
    layout = 'default',
    placeholder,
    withSearch,
    tooltipContent,
    virtualized,
    ...props
  }: Props) => {

    const [isExpanded, setIsExpanded] = useState(false);
    const [referenceElement, setReferenceElement] = useState(null);
    const [forceClose, setForceClose] = useState(false);
    const { shouldRender, isAnimating } = useDelayedUnmount(isExpanded && !forceClose, forceClose ? 0 : 400);

    // Listen for custom event to close this dropdown when another opens
    useEffect(() => {
      const handleCloseAll = () => {
        if (isExpanded) {
          setForceClose(true);
          setIsExpanded(false);
        }
      };
      document.addEventListener('dropdown:close-all', handleCloseAll);
      return () => document.removeEventListener('dropdown:close-all', handleCloseAll);
    }, [isExpanded]);

    // Reset forceClose when dropdown is fully closed
    useEffect(() => {
      if (!isExpanded && forceClose) {
        setForceClose(false);
      }
    }, [isExpanded, forceClose]);

    className = classNames('dropdown-input input', className, {
      focus: isFocused,
      hover: isHovered,
      active: isExpanded
    });

    const inputValue = typeof value === 'object' ? value?.value : value;
    const displayValue =
      (find(items, ['value', inputValue]) as DropdownItemObject)?.label || // if value is string for object items
      (typeof value === 'object' ? value?.label : value)                   // if value is object it already has the label

    const showFeedbackElem = !!(showFeedback && feedback);
    const feedbackElem = (persistentFeedback || showFeedbackElem) && (
      <div
        className={classNames(
          'feedback input-feedback',
          kebabCase(feedbackPosition!),
          { 'feedback-visible': showFeedbackElem }
        )}
        data-status={status}
        aria-live="polite">
        {showFeedback && feedback}
      </div>
    );

    const labelElem = label && (
      <label className={['input-label', kebabCase(labelPosition)].join(' ')}>
        {label}

        {tooltipContent &&
          <Tooltip
            content={tooltipContent}
            className="popup-tip"
            trigger="click">
            <Button className="info-button" icon={<InfoSVGIcon />} />
          </Tooltip>}
      </label>
    );

    let inputElem;

    const handleInputElemClick = (_evt: React.MouseEvent) => {
      // Dispatch a custom event to close other dropdowns before opening this one
      if (!isExpanded) {
        document.dispatchEvent(new CustomEvent('dropdown:close-all'));
      }
      setIsExpanded(!isExpanded);
      // Remove focus from the input element in order to avoid click event interruptions in some scenarios
      (referenceElement as HTMLElement | null)?.blur();
    }

    switch (layout) {
      case 'filter':
        inputElem = (
          <button
            className="sort-field-btn sort btn-core btn filter"
            type="button"
            ref={setReferenceElement as React.Ref<HTMLButtonElement>}
            disabled={disabled}
            onClick={handleInputElemClick}
            onFocus={props.onFocus}
            onBlur={props.onBlur}
            onPointerEnter={props.onPointerEnter}
            onPointerLeave={props.onPointerLeave}
            data-guided-training-id={props['data-guided-training-id']}>
            {value ? (
              <span className="value">{displayValue}</span>
            ) : (
              !disabled && <span className="placeholder">{placeholder}</span>
            )}
            <span className="icon">
              {isExpanded ? <ChevronUpSVGIcon /> : <ChevronDownSVGIcon />}
            </span>
          </button>
        );
        break;
      case 'plain':
        inputElem = (
          <button
            className={'dropdown-button' + (isExpanded ? ' is-open' : '')}
            type="button"
            ref={setReferenceElement as React.Ref<HTMLButtonElement>}
            disabled={disabled}
            onClick={handleInputElemClick}
            onFocus={props.onFocus}
            onBlur={props.onBlur}
            onPointerEnter={props.onPointerEnter}
            onPointerLeave={props.onPointerLeave}
            data-guided-training-id={props['data-guided-training-id']}>
            {value ? (
              <span className="value">{displayValue}</span>
            ) : (
              <span className="placeholder">{!disabled && placeholder}</span>
            )}
            <span className="icon">
              <ChevronDownSVGIcon />
            </span>
          </button>
        );
        break;
      case 'default':
      default:
        inputElem = (
          <div
            className={className}
            aria-disabled={disabled}
            data-guided-training-id={props['data-guided-training-id']}>
            <div className="dropdown-input-box input-box" />
            <button
              className={'dropdown-button' + (isExpanded ? ' is-open' : '')}
              type="button"
              ref={setReferenceElement as React.Ref<HTMLButtonElement>}
              disabled={disabled}
              onClick={handleInputElemClick}
              onFocus={props.onFocus}
              onBlur={props.onBlur}
              onPointerEnter={props.onPointerEnter}
              onPointerLeave={props.onPointerLeave}>
              {value ? (
                <span className="value">{displayValue}</span>
              ) : (
                <span className="placeholder">{!disabled && placeholder}</span>
              )}
              <span className="icon">
                <ChevronDownSVGIcon />
              </span>
            </button>
            {labelElem}
            {feedbackElem}
          </div>
        );
        break;
    }

    return (
      <>
        {inputElem}

        {shouldRender && (
          <MenuItems
            className={classNames('menu-animate', isAnimating)}
            items={items}
            referenceElement={referenceElement}
            value={inputValue}
            onChange={(evt, val) => {
              if (props.notDeselectable && val === value) return;
              props.onChange?.(evt, val);
              setIsExpanded(false);
            }}
            onOutsideClick={() => {
              setIsExpanded(false);
            }}
            withSearch={withSearch}
            virtualized={virtualized}
          />
        )}
      </>
    );
  }
);

type ControlledProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> & {
  value?: string;
  label?: string;
  validator?: 'top' | 'bottom' | 'none';
  model: InputState;
  showEmptyItem?: Maybe<boolean>;
  showFeedback?: Maybe<boolean>;
  className?: string | ObjectLiteral;
  dropdownClassName?: string | ObjectLiteral;
  layout?: DropdownLayout;
  feedbackPosition?: VerticalPosition;
  persistentFeedback?: boolean;
  notDeselectable?: boolean;
  withSearch?: boolean;
  tooltipContent?: string | ReactNode;
  virtualized?: boolean;
};

/**
 * Wrapper for SelectInput in which the entire state and all the handlers are provided through an InputState object.
 */
export const DropdownInputControlled = observer(
  ({
    model,
    className,
    dropdownClassName,
    showFeedback: _showFeedback = true,
    ...props
  }: ControlledProps) => {
    assert(
      model instanceof InputState,
      `The 'model' passed to CustomDropdownInputControlled must be an instance of InputState. Instead received ${model}.`
    );

    const handleChange = useCallback(
      (evt: React.SyntheticEvent, value: string) => {
        if (model.normValue === value) {
          if (props.notDeselectable) return;

          model.handleChange(evt, null);
        } else {
          model.handleChange(evt, value || null);
        }
      },
      [model, props.notDeselectable]
    );

    className = model.getClassName('dropdown-input-controlled', { className });

    return (
      <DropdownInput
        className={className}
        dropdownClassName={dropdownClassName}
        value={model.value || null}
        placeholder={model.placeholder}
        disabled={model.disabled}
        label={model.label}
        items={model.selectorItems as DropdownItem[]}
        showEmptyItem={true}
        isHovered={model.isHovered}
        isFocused={model.isFocused}
        tooltipContent={props.tooltipContent}
        onChange={handleChange}
        onFocus={model.handleFocus}
        onBlur={model.handleBlur}
        onPointerEnter={model.handlePointerEnter}
        onPointerLeave={model.handlePointerLeave}
        status={model.status}
        statusMessage={model.statusMessage}
        showStatus={model.showStatus}
        showFeedback={model.showStatusMessage}
        {...props}
      />
    );
  }
);
